import { CommonModule } from '@angular/common';
import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroEye, heroEyeSlash } from '@ng-icons/heroicons/outline';
import { ToastrService } from 'ngx-toastr';
import { finalize, first, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../modules/auth';
import { AfterLogin } from '../../modules/auth/services/@core/interfaces/common/users';
import { AccountListCacheService } from '../../@theme/services/account-list-cache.service';
import { InitUserService } from '../../@theme/services/init-user.service';
import { PendingTransactionViewState } from '../../models/viewState.model';
import { TransactionStateService } from '../../pages/transaction-list/transaction-state.service';
const helper = new JwtHelperService();
@Component({
  selector: 'app-sign-in',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, NgIcon],
  providers: [provideIcons({ heroEye, heroEyeSlash })],
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.scss',
})
export class SignInComponent implements OnInit, OnDestroy {
  showPassword = false;
  public isLoading = false;
  signInForm: FormGroup;
  error: string | undefined;
  errors: string[] = [];
  subscription: Subscription | undefined;
  showMessages: boolean | undefined;
  showdanger: boolean | undefined;
  version = environment.appVersion;
  assetsPath = environment.assetsPath;
  
  // Verification message properties
  showVerificationMessage = false;
  verificationMessage = '';
  private verificationMessageTimeout: any;
  
  private _alive = true;
  public get alive() {
    return this._alive;
  }
  public set alive(value) {
    this._alive = value;
  }

  private decodedToken: AfterLogin | undefined;

  public show: boolean | undefined;

  get username() {
    return this.signInForm.get('username');
  }

  get password() {
    return this.signInForm.get('password');
  }
  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }
  ngOnInit(): void {
    // Check if redirected due to session expiration
    this.route.queryParams.subscribe(params => {
      if (params['sessionExpired'] === 'true') {
        this.toastr.warning('Your session has expired. Please log in again.', 'Session Expired', {
          timeOut: 5000,
          positionClass: 'toast-top-right'
        });
        // Clear the query param from URL
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true
        });
      }
    });
  }
  @HostBinding('class.dark') isDarkMode = false;
  constructor(
    private fb: FormBuilder,
    private renderer: Renderer2,
    protected auth: AuthService,
    protected router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private initUserService: InitUserService,
    private accountListCache: AccountListCacheService,
    private transactionState: TransactionStateService
  ) {
   // localStorage.clear();
    localStorage.removeItem('rl');
    localStorage.removeItem('rpd');
    localStorage.removeItem('rrolelisted');
    localStorage.removeItem('rvarietylist');
    // Wipe any cached account list (PII) whenever the login screen loads, in
    // case the previous session ended without a clean logout (e.g. expiry).
    void this.accountListCache.clear();
    // Start each login with a clean Pending Transactions filter/sort/view.
    // Resets both the persisted copy (localStorage) and the in-memory singleton.
    this.initUserService.setPendingTransactionViewState(
      new PendingTransactionViewState(),
    );
    // All Transactions: Status back to Approved, Transaction ID blank.
    this.transactionState.clearSessionFilters();
    this.isLoading = false;
    this.signInForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(4)]],
      password: ['', [Validators.required, Validators.minLength(4)]], // Changed from 10 to 4
      //   rememberMe: [false],
      email: [''],
    });
  }
  onSubmit() {
    if (this.signInForm.invalid || this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.signInForm.value.email = this.signInForm.value.username;
    if (this.signInForm.valid) {
      this.auth
        .login(this.signInForm.value)
        .pipe(
          first(),
          // Always release the spinner. The Sign In button is hidden while
          // isLoading is true, so any path that leaves it set locks the user out
          // of the login screen entirely.
          finalize(() => (this.isLoading = false))
        )
        .subscribe({
          next: (result) => this.verified(result),
          error: (error) => {
            this.errorunverified(error);
          },
        });
    }
  }
  ouputfornewUsers() {
    this.toastr.success(
      'An email has been sent. Please check your inbox to continue registration',
      'success'
    );
    this.show = true;
    this.router.navigateByUrl('/auth/login');
  }
  verified(result?: {
    isLockedOut?: boolean;
    token?: string;
    isSuccess?: boolean;
  }) {

    if (result?.token === "verified") {
      // Show verification message for account upgrade
      this.showVerificationMessage = true;
      this.verificationMessage = 'Thank you for updating your account from 1.0 to 2.0. Please check your email to get new login credentials.';
      
      // Hide message after 20 seconds with fade-out animation
      this.verificationMessageTimeout = setTimeout(() => {
        // Add fade-out class for smooth animation
        const messageElement = document.querySelector('.verification-message');
        if (messageElement) {
          messageElement.classList.remove('animate-fade-in');
          messageElement.classList.add('animate-fade-out');
          
          // Hide completely after animation finishes
          setTimeout(() => {
            this.showVerificationMessage = false;
            this.verificationMessage = '';
          }, 500); // 0.5s animation duration
        } else {
          this.showVerificationMessage = false;
          this.verificationMessage = '';
        }
      }, 20000);
      
      return;
    }
    if (result === undefined) {
      this.toastr.error('Username or password incorrect', 'Error!');
      return;
    }
    if (result?.isLockedOut) {
      this.toastr.error('Your account is currently locked out.', 'Locked Out!');
      return;
    }

    const decodedToken = result?.token
      ? helper.decodeToken(result.token)
      : undefined;
    this.decodedToken = decodedToken;

    if (decodedToken?.currentrole) {
      sessionStorage.setItem('rl', decodedToken.currentrole);
      localStorage.setItem('rl', decodedToken.currentrole);
    }
    /*   
    if (decodedToken?.token === undefined) {
      this.handleVerificationFailure();
      return;
    } */

    if (decodedToken?.EmailConfirmed === 'False') {
      this.handleEmailNotConfirmed();
      return;
    }

    if (
      decodedToken?.EmailConfirmed === 'True' &&
      decodedToken?.PhoneNumberConfirmed === 'false' &&
      !decodedToken?.PhoneNumber
    ) {
      this.handlePhoneNotConfirmed();
      return;
    }

    // No usable token means we genuinely cannot continue.
    if (!decodedToken) {
      this.handleVerificationFailure();
      return;
    }

    if (decodedToken.AuthyStatus === '3') {
      this.handleSuccessfulAuth();
      return;
    }

    // Anything else -- '1' (needs a factor), '2' (code already sent), null for a
    // user who never registered a factor, or any value we don't recognise -- means
    // "not verified yet", so send them to the verification screen. Previously only
    // '1' was handled and every other value fell through to a dead end that left
    // the spinner up with no Sign In button.
    this.router.navigateByUrl('/auth/verify-user');
  }

  private handleEmailNotConfirmed() {
    sessionStorage.setItem('oldy', this.signInForm.value.password);
    this.router.navigateByUrl('/auth/verify-user');
  }

  private handlePhoneNotConfirmed() {
    sessionStorage.setItem('oldy', this.signInForm.value.password);
    this.router.navigateByUrl('/auth/verify-user');
  }

  private handleSuccessfulAuth() {
    // this.initUserService.initCurrentUser())
    this.router.navigateByUrl('pages/dashboard');
  }

  private handleVerificationFailure() {
    // Reached only when the response carried no usable token. The form stays
    // enabled (isLoading is released by finalize) so the user can retry.
    console.error('Sign-in returned no usable token', this.decodedToken);
    this.error =
      'Sign-in could not be completed. Please try again, or contact your administrator if this continues.';
    this.toastr.error(this.error, 'Sign-in failed');
    this.showMessages = true;
  }

  errorunverified(error: { name: string; error: { isLockedOut: boolean } }) {
    if (error.name.toLocaleLowerCase() === 'error'.toLocaleLowerCase()) {
      this.toastr.error(this.GetErrors(0), 'Error!');
      this.showMessages = true;
      this.showdanger = false;
      this.errors['1'].toString();
      return;
    }
    if (error.error['status'] === 401) {
      this.toastr.error(this.GetErrors(0), 'Error!');
      return;
    }
    if (error.error.isLockedOut === true) {
      //  console.log(JSON.stringify(error.error));
      this.toastr.error(this.GetErrors(1), 'Error!');
      this.showMessages = true;
      this.showdanger = false;
      this.errors['1'].toString();
      return;
    }
    this.toastr.error(this.GetErrors(0), 'Error!');
    this.showMessages = true;
    this.showdanger = false;
    this.errors['0'].toString();
    return;
  }
  GetErrors(n: number) {
    this.errors = [
      'Username and password combination is not correct, please try again.',
      'Your are currently Locked Out, Please contact your administrator!',
      'To many attempts has been made. Try again in 10 mins.',
    ];
    this.error = this.errors[n];
    return this.errors[n];
  }
  getpasswordcomponent(event: Event) {
    if (event) {
      event.preventDefault();
    }
  }
  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    if (this.verificationMessageTimeout) {
      clearTimeout(this.verificationMessageTimeout);
      this.verificationMessageTimeout = undefined;
    }
  }
}
